import {
  makeContentServicePostRequest,
  makeContentServiceGetRequest,
} from 'infrastructure/contentService'
import { getFlowResults, isRequestError } from '../../contentService/utility'
import type { ContentServiceFlowResults } from '../../contentService/types'
import {
  SlidesPrepareStartResponse,
  SlidesPrepareFlowIdResponse,
  SlidesProcessStartResponse,
  SlidesProcessFlowIdResponse,
  SlidesPrepareWorkerLessons,
} from './types'

/**
 * Start the slides prepare stage.
 */
export async function startSlidesPrepare(
  treeId: string,
  treeVer: string,
  googleAccessToken: string,
): Promise<SlidesPrepareStartResponse> {
  const response = await makeContentServicePostRequest(
    `content/v1/slides/${treeId}:${treeVer}/prepare/start`,
    { google_access_token: googleAccessToken },
  )

  if (!response.ok) {
    const rawResponse = await response.text()
    const error = new Error(rawResponse) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  return response.json()
}

/**
 * Get the existing flow ID for a slides prepare stage.
 */
export async function getSlidesPrepareFlowId(
  treeId: string,
  treeVer: string,
): Promise<SlidesPrepareFlowIdResponse | null> {
  const response = await makeContentServiceGetRequest(
    `content/v1/slides/${treeId}:${treeVer}/prepare/flow_id`,
  )

  if (!response.ok) {
    return null
  }

  return response.json()
}

/**
 * Start the slides process stage (stages 2–5).
 */
export async function startSlidesProcess(
  treeId: string,
  treeVer: string,
  googleAccessToken: string,
  directoryId: string,
  lessons: SlidesPrepareWorkerLessons,
): Promise<SlidesProcessStartResponse> {
  const response = await makeContentServicePostRequest(
    `content/v1/slides/${treeId}:${treeVer}/process/start`,
    {
      google_access_token: googleAccessToken,
      directory_id: directoryId,
      lessons,
    },
  )

  if (!response.ok) {
    const rawResponse = await response.text()
    const error = new Error(rawResponse) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  return response.json()
}

/**
 * Get the existing flow ID for a slides process stage.
 */
export async function getSlidesProcessFlowId(
  treeId: string,
  treeVer: string,
): Promise<SlidesProcessFlowIdResponse | null> {
  const response = await makeContentServiceGetRequest(
    `content/v1/slides/${treeId}:${treeVer}/process/flow_id`,
  )

  if (!response.ok) {
    return null
  }

  return response.json()
}

/**
 * Fetch the full prepare worker result to get the lessons dict and directory_id
 * needed for process/start.
 */
export async function fetchPrepareWorkerLessons(
  treeId: string,
  treeVer: string,
): Promise<{ directoryId: string; lessons: SlidesPrepareWorkerLessons }> {
  const flowIdResponse = await getSlidesPrepareFlowId(treeId, treeVer)
  if (!flowIdResponse?.data) {
    throw new Error('Prepare flow not found. Complete the prepare stage first.')
  }

  const flowResults = await getFlowResults<ContentServiceFlowResults>(
    flowIdResponse.data,
  )
  if (isRequestError(flowResults)) {
    throw new Error(
      `Failed to fetch prepare flow results: ${flowResults.errorText}`,
    )
  }

  const workerResult = flowResults.data?.worker_results?.[0]?.result as
    | {
        directory_id?: string
        lessons?: SlidesPrepareWorkerLessons
      }
    | undefined
  if (!workerResult?.directory_id || !workerResult?.lessons) {
    throw new Error('Prepare worker result missing directory_id or lessons')
  }

  return {
    directoryId: workerResult.directory_id,
    lessons: workerResult.lessons,
  }
}
